import React from 'react';
import { Box, Paper, Typography, Chip } from '@mui/material';
import { DragIndicator } from '@mui/icons-material';
import { neutral, primary } from '_lex/_foundations/phxColors';
import { translate as t } from 'hooks/useTranslations';
import { AWB } from '../../../../../_types/awb';

interface DraggableItemProps {
  item: AWB.Node;
  title: string;
  itemType: 'standard' | 'subStandard' | 'requirement';
  isSelected?: boolean;
  handleStandardClick?: (standardId: string) => void;
}

const DraggableItem: React.FC<DraggableItemProps> = ({
  item,
  title,
  itemType,
  isSelected = false,
  handleStandardClick,
}) => {
  const handleItemClick = () => {
    if (handleStandardClick) {
      handleStandardClick(item.id);
    }
  };

  const getTypeLabel = () => {
    switch (itemType) {
      case 'standard':
        return t('awb.standards.new.standard');
      case 'subStandard':
        return t('awb.standards.new.subStandard');
      case 'requirement':
        return t('awb.standards.new.requirement');
      default:
        return '';
    }
  };

  const getItemTitle = () => {
    const heading = item.heading ? `${item.heading} ` : '';
    return `${heading}${title}`;
  };

  return (
    <Paper
      sx={{
        p: 2,
        mb: 1,
        cursor: 'pointer',
        backgroundColor: isSelected ? primary[50] : 'white',
        borderLeft: isSelected ? `3px solid ${primary[500]}` : 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        '&:hover': {
          backgroundColor: isSelected ? primary[50] : neutral[50],
        },
      }}
      onClick={handleItemClick}
    >
      <Box sx={{ flex: 1, minWidth: 0, minHeight: '40px', display: 'flex', alignItems: 'center' }}>
        <Box sx={{ display: 'none', alignItems: 'center', gap: 1, mb: 1 }}>
          <Chip
            label={getTypeLabel()}
            size='small'
            variant='outlined'
            sx={{
              fontSize: '0.75rem',
              height: '20px',
              backgroundColor: primary[50],
              borderColor: primary[200],
              color: primary[700],
            }}
          />
        </Box>

        <Typography
          variant='bodySm'
          sx={{
            fontWeight: 500,
            color: neutral[900],
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {getItemTitle()}
        </Typography>
      </Box>
    </Paper>
  );
};

export default DraggableItem;
